import { useEffect, useRef, useState } from "react";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Inbox, Settings, FileText, Briefcase, Quote, PoundSterling, Newspaper, LogOut, ExternalLink, ShieldAlert, Search } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { http, formatError } from "@/lib/api";
import { Enquiries } from "./admin/Enquiries";
import { ContentManager } from "./admin/ContentManager";
import { SettingsEditor } from "./admin/SettingsEditor";
import { BlogList } from "./admin/blog/BlogList";
import { BlogEditor } from "./admin/blog/BlogEditor";
import { BlogPreview } from "./admin/blog/BlogPreview";
import { SearchPerformance } from "./admin/SearchPerformance";

const TABS = [
  ["posts", "Blog", Newspaper], ["search", "Search performance", Search], ["enquiries", "Enquiries", Inbox], ["services", "Service pages", FileText], ["case_studies", "Case studies", Briefcase],
  ["testimonials", "Testimonials", Quote], ["pricing", "Pricing", PoundSterling], ["settings", "Settings", Settings],
];

const startGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Login = ({ error }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#13052A] px-5">
    <div className="w-full max-w-sm rounded-[28px] bg-white p-8" data-testid="admin-login">
      <img src="/img/logo-dark.png" alt="Digital AdDoctor" className="h-14 w-auto" />
      <h1 className="mt-6 text-2xl font-extrabold text-[#13052A]">Admin sign in</h1>
      <p className="mt-2 text-sm text-slate-600">Sign in with your approved Google account to manage the website.</p>
      {error && <p className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700" data-testid="admin-login-error"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />{error}</p>}
      <button onClick={startGoogleLogin} className="btn-primary mt-6 w-full" data-testid="admin-google-login">Sign in with Google</button>
      <Link to="/" className="mt-4 block text-center text-xs font-semibold text-slate-500 hover:text-[#581C87]">Back to website</Link>
    </div>
  </div>
);

const Spinner = () => <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#7C3AED]" /></div>;

// Exchanges the one-time session_id from Google sign-in for a secure session cookie.
const AuthCallback = ({ onDone }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    http.post("/auth/session", { session_id: sid })
      .then((r) => onDone(r.data, ""))
      .catch((e) => onDone(false, formatError(e)))
      .finally(() => navigate("/admin", { replace: true }));
  }, [location.hash, navigate, onDone]);
  return <Spinner />;
};

const Dashboard = ({ user, logout }) => {
  const [tab, setTab] = useState("posts");
  return (
    <div className="min-h-screen bg-[#F5F0FF] lg:flex" data-testid="admin-dashboard">
      <aside className="bg-[#13052A] p-5 text-white lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:shrink-0">
        <img src="/img/logo-light.png" alt="Digital AdDoctor" className="h-12 w-auto" />
        <nav className="mt-8 flex gap-1 overflow-x-auto lg:flex-col">
          {TABS.map(([k, label, I]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`flex shrink-0 items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors ${tab === k ? "bg-[#7C3AED] text-white" : "text-violet-100 hover:bg-white/5"}`}>
              <I className="h-4 w-4" /> {label}
            </button>
          ))}
        </nav>
        <div className="mt-4 flex gap-4 lg:mt-8 lg:block lg:space-y-1">
          <Link to="/" target="_blank" className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-violet-200 hover:text-white"><ExternalLink className="h-3.5 w-3.5" /> View website</Link>
          <button onClick={logout} className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-violet-200 hover:text-white" data-testid="admin-logout"><LogOut className="h-3.5 w-3.5" /> Log out</button>
        </div>
      </aside>
      <section className="min-w-0 flex-1 p-5 sm:p-10">
        <p className="mb-6 text-sm text-slate-600">Signed in as <span className="font-bold text-[#13052A]" data-testid="admin-user-email">{user.email}</span></p>
        {tab === "posts" && <BlogList />}
        {tab === "search" && <SearchPerformance />}
        {tab === "enquiries" && <Enquiries />}
        {tab === "settings" && <SettingsEditor />}
        {!["posts", "search", "enquiries", "settings"].includes(tab) && <ContentManager col={tab} />}
      </section>
    </div>
  );
};

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const callback = location.hash?.includes("session_id=");

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setUser(false);
  };
  const onDone = (u, err) => { setUser(u); setError(err); };

  if (callback) return <AuthCallback onDone={onDone} />;
  if (user === null) return <Spinner />;
  return (
    <>
      <Seo title="Admin" path="/admin" noindex />
      {!user ? <Login error={error} /> : (
        <Routes>
          <Route path="blog/:id" element={<BlogEditor />} />
          <Route path="preview/:id" element={<BlogPreview />} />
          <Route path="*" element={<Dashboard user={user} logout={logout} />} />
        </Routes>
      )}
    </>
  );
}
